import * as sharedTypes from 'src/actions/shared/sharedTypes';
import Actions from 'src/actions/rules/actions';
import { AttributeType, conditionClassFromType, ConditionType } from 'src/actions/rules/condition';
import {
  ContainsValueMatcher,
  ExactValueMatcher,
  MatcherType,
  RegexMatcher,
  WordsMatcher,
} from 'src/actions/rules/string_matcher';
import rulesResource, { Rule } from 'src/actions/rules/rulesResource';
import validateType, { translateErrors } from 'src/adapters/validateType';
import { DomainError } from '@ad-scripts/application/lib/error';

const StringMatcherConditions = new Set([
  ConditionType.ChatURL,
  ConditionType.CustomAttribute,
  ConditionType.MessageBody,
  ConditionType.MessageRecipient,
  ConditionType.MessageSender,
  ConditionType.MessageSubject,
]);

const validateRule: sharedTypes.AppThunk = (draft: any) => dispatch => {
  const validationError = rulesResource.actions.validationError;
  let errors: any[] = [];

  // validate rule
  try {
    validateType(Rule, draft);
  } catch (err) {
    const ruleErrors = translateErrors((err as DomainError).errors);
    errors = errors.concat(ruleErrors);
  }

  // validate actions because we cannot both validate nested and top-level emptiness in same place
  try {
    validateType(Actions, draft.actions);
  } catch (err) {
    const actionErrors = translateErrors((err as DomainError).errors).map(e => {
      return { ...e, attr: `actions.${e.attr}` };
    });
    errors = errors.concat(actionErrors);
  }

  // validate conditions separately because they comprise various types
  for (const i in draft.conditions) {
    const condition = draft.conditions[i];
    const model = conditionClassFromType(condition.type, condition.attrType);
    try {
      validateType(model, condition);
    } catch (err) {
      const conditionErrors = translateErrors((err as any).errors).map(e => {
        return { ...e, attr: `conditions[${i}].${e.attr}` };
      });
      errors = errors.concat(conditionErrors);
    }
    // if condition uses string matcher, validate subtype.
    // since we need to check prop names rather than prop values (e.g. a type prop),
    // we cannot use the nested validator with discriminator
    if (
      StringMatcherConditions.has(condition.type) &&
      (condition.attrType == null || condition.attrType === AttributeType.String)
    ) {
      const hasNot = !!condition.value.not;
      const matcher = hasNot ? condition.value.not : condition.value;
      let matcherClass: any;
      let matcherDraft: any;
      let matcherType: MatcherType;

      if (matcher.regex !== undefined) {
        matcherClass = RegexMatcher;
        matcherDraft = matcher.regex;
        matcherType = MatcherType.Regex;
      } else if (matcher.exact !== undefined) {
        matcherClass = ExactValueMatcher;
        matcherDraft = matcher.exact;
        matcherType = MatcherType.Exact;
      } else if (matcher.words !== undefined) {
        matcherClass = WordsMatcher;
        matcherDraft = matcher.words;
        matcherType = MatcherType.Words;
      } else {
        matcherClass = ContainsValueMatcher;
        matcherDraft = matcher.contains;
        matcherType = MatcherType.Contains;
      }

      try {
        validateType(matcherClass, matcherDraft);
      } catch (err) {
        const conditionErrors = translateErrors((err as any).errors).map(e => {
          return {
            ...e,
            attr: `conditions[${i}].value.${hasNot ? 'not.' : ''}${matcherType}.${e.attr}`,
          };
        });
        errors = errors.concat(conditionErrors);
      }
    }
  }

  if (errors.length) {
    dispatch(validationError(errors));
    return false;
  }

  return true;
};

export default validateRule;
